import React, {useState, useCallback} from 'react';
import {useNavigate, useParams} from "react-router-dom";
import Box from '@mui/material/Box';
import Grid from "@mui/material/Grid";
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import Typography from "@mui/material/Typography";
import { useForm, SubmitHandler } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";

import {useAuth} from "../../_auth/useAuth";
import {useLocation} from "react-router-dom";
import {
    Form,
    DaButton,
    DaTextField,
    ProgressPanel,
    MessagePanel,
    IMessagePanelProps,
    ButtonBar,
} from "../_shared";
import {FormPaper} from "./PasswordReset.styles";

import {useTheme} from "@mui/material/styles";
import {passwordResetFields} from "../../_appData";
import {AuthConfirmPasswordResetOptions, AuthPasswordResetOptions} from "../../_auth/types";

import {Location} from "history";
import {IPasswordReset} from "../../_api";

interface loginState {
    returnTo: Location
};

const signupSchema: yup.SchemaOf<IPasswordReset> = yup.object({
    email: yup.string().label(passwordResetFields.email.label).max(254).required(),
});

export const PasswordResetView: React.FC = () => {
    const theme = useTheme();
    const { passwordReset } = useAuth();
    const navigate = useNavigate();
    const params = useParams();
    const [msgPanelProps, setMsgPanelProps] = useState<IMessagePanelProps>({message: ""});
    const [progressTitle, setProgressTitle] = useState<string>("Processing Signup Info...");
    const [isShowError, setIsShowError] = useState<boolean>(false);
    const [showProgress, setShowProgress] = useState<boolean>(false);

    // legacy state values, need to refactor
    const { state } = useLocation();

    const formFieldSize = "medium";
    const formFieldVariant = "standard";

    // defining the initial state for the form
    const initialValues = {
        email: '',
    };

    const { control, handleSubmit, formState: { errors } } = useForm<IPasswordReset>({
        defaultValues: initialValues,
        resolver: yupResolver(signupSchema)
    });

    const showError = ({title, message, type="error"}: IMessagePanelProps) => {
        const msgProps: IMessagePanelProps = {
            message: message,
            title: title,
            type: type
        }
        setMsgPanelProps(msgProps);
        setIsShowError(true);
    }

    // a submit function that will execute upon form submission
    const onSubmit: SubmitHandler<IPasswordReset> = (data) => {
        submitResetData(data);
    };

    const submitResetData = useCallback(async (options: AuthPasswordResetOptions): Promise<void> => {
        setProgressTitle("Performing Password Reset...");
        setShowProgress(true);

        let apiPromise = undefined;
        apiPromise = passwordReset(options);

        apiPromise && apiPromise.then((resp) => {
            if (resp && resp.state === "success"){
                // get options to pass to confirmation route
                const { email} = options;
                let confirm_options: AuthConfirmPasswordResetOptions = {
                    email: email,
                    password: "",
                    confirmPassword: "",
                    code: "",
                }
                navigate("/password-reset-confirm", { state: confirm_options });
            } else {
                showError({
                    title: "Unable to Perform Password Reset",
                    message: "An error occurred while saving Password Reset data. " +
                        "Verify that you have provided all required data and " +
                        "retry your last action."
                });
            }
        })
        .catch(() => {
            showError({
                title: "PasswordReset API Save Error",
                message: "An error occurred while saving Password Reset data. " +
                    "There may have been a network error.",
            });
        })
        .finally(() => {
            setShowProgress(false);
        });
    }, [passwordReset, navigate]);


    return (
        <>
            <FormPaper elevation={4}>
                <Box display="flex" flexDirection="column" alignItems="center">
                    <AccountCircleIcon
                        sx={{
                            fontSize: 68,
                            color: theme.palette.grey["500"],
                            mb: 2
                        }}
                    />
                    <Typography variant="h4">Password Reset</Typography>
                </Box>

                <Grid container>
                    {isShowError ? (
                        <Grid item xs={12} justifyContent={"start"}>
                            <MessagePanel
                                {...msgPanelProps}
                            />
                        </Grid>
                    ) : null}
                    {showProgress ? (
                        <Grid item xs={12}>
                            <ProgressPanel title={progressTitle}/>
                        </Grid>
                    ) : (
                        <Grid container item xs={12}>
                            <Form
                                autoComplete="off"
                                onSubmit={handleSubmit(onSubmit)}
                            >
                                <Grid container spacing={theme.spacing(1)}>
                                    <Grid item xs={12} md={12}>
                                        <DaTextField
                                            control={control}
                                            name={passwordResetFields.email.name}
                                            label={passwordResetFields.email.label}
                                            required={passwordResetFields.email.required}
                                            variant={formFieldVariant}
                                            size={formFieldSize}
                                            errors={errors}
                                        />
                                    </Grid>
                                </Grid>
                                <Grid container spacing={theme.spacing(1)}>
                                    <Grid item xs={12} md={12}>
                                        <ButtonBar buttonAlignment="alignRight">
                                            <DaButton buttonStyle="primary" type="submit">
                                                Submit
                                            </DaButton>
                                        </ButtonBar>
                                    </Grid>
                                </Grid>
                            </Form>
                        </Grid>
                    )}
                </Grid>
            </FormPaper>
        </>
    );
}